import { doc, setDoc, getDocs, collection, updateDoc } from 'firebase/firestore';
import { db } from './config';
import { apartments as staticApartments } from '../data/apartments';

export class MigrationService {
  /**
   * Update construction years for all apartments
   * Haus A (A1-A11): Frühjahr 2028
   * Haus B (B1-B11): Herbst 2027
   */
  static async updateConstructionYears(): Promise<void> {
    console.log('🚀 Starting construction year update...');
    
    try {
      let updatedCount = 0;
      let errorCount = 0;

      // Update Haus A (A1 to A11) - Frühjahr 2028
      for (let i = 1; i <= 11; i++) {
        try {
          const docId = `A${i}`;
          const docRef = doc(db, 'apartments', docId);
          
          await updateDoc(docRef, {
            constructionYear: "Frühjahr 2028",
            updatedAt: new Date()
          });
          
          console.log(`✅ Updated ${docId}: Frühjahr 2028`);
          updatedCount++;
        } catch (error) {
          console.error(`❌ Error updating ${`A${i}`}:`, error);
          errorCount++;
        }
      }

      // Update Haus B (B1 to B11) - Herbst 2027
      for (let i = 1; i <= 11; i++) {
        try {
          const docId = `B${i}`;
          const docRef = doc(db, 'apartments', docId);
          
          await updateDoc(docRef, {
            constructionYear: "Herbst 2027",
            updatedAt: new Date()
          });
          
          console.log(`✅ Updated ${docId}: Herbst 2027`);
          updatedCount++;
        } catch (error) {
          console.error(`❌ Error updating ${`B${i}`}:`, error);
          errorCount++;
        }
      }

      console.log(`🎉 Construction year update completed!`);
      console.log(`✅ Successfully updated: ${updatedCount} apartments`);
      if (errorCount > 0) {
        console.log(`❌ Errors: ${errorCount} apartments`);
      }

    } catch (error) {
      console.error('❌ Construction year update failed:', error);
      throw error;
    }
  }
  /**
   * Migrate static apartment data to Firestore
   */
  static async migrateApartmentsToFirestore(): Promise<void> {
    console.log('🚀 Starting apartment migration to Firestore...');
    
    try {
      let migratedCount = 0;
      let errorCount = 0;

      for (const apartment of staticApartments) {
        try {
          const docId = `apartment_${apartment.id}`;
          const apartmentData = {
            ...apartment,
            createdAt: new Date(),
            updatedAt: new Date(),
            migratedAt: new Date()
          };

          // Use setDoc instead of addDoc to control document IDs
          await setDoc(doc(db, 'apartments', docId), apartmentData);
          
          console.log(`✅ Migrated: ${apartment.name} (ID: ${apartment.id})`);
          migratedCount++;
        } catch (error) {
          console.error(`❌ Error migrating apartment ${apartment.id}:`, error);
          errorCount++;
        }
      }

      console.log(`🎉 Migration completed!`);
      console.log(`✅ Successfully migrated: ${migratedCount} apartments`);
      if (errorCount > 0) {
        console.log(`❌ Errors: ${errorCount} apartments`);
      }

    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  /**
   * Check if apartments already exist in Firestore
   */
  static async checkExistingData(): Promise<{ count: number; hasData: boolean }> {
    try {
      const querySnapshot = await getDocs(collection(db, 'apartments'));
      const count = querySnapshot.size;
      
      return {
        count,
        hasData: count > 0
      };
    } catch (error) {
      console.error('Error checking existing data:', error);
      return { count: 0, hasData: false };
    }
  }

  /**
   * Initialize default settings in Firestore
   */
  static async initializeSettings(): Promise<void> {
    const defaultSettings = {
      general: {
        siteName: 'Wildbach Wohnungsfinder',
        contactEmail: 'info@wildbach-hinwil.ch',
        maintenanceMode: false,
        updatedAt: new Date()
      },
      admin: {
        lastMigration: new Date(),
        version: '1.0.0',
        features: {
          liveUpdates: true,
          realTimeSync: true
        }
      }
    };

    try {
      for (const [key, value] of Object.entries(defaultSettings)) {
        await setDoc(doc(db, 'settings', key), value);
        console.log(`✅ Initialized setting: ${key}`);
      }
    } catch (error) {
      console.error('❌ Error initializing settings:', error);
      throw error;
    }
  }

  /**
   * Complete setup - migration + settings
   */
  static async completeSetup(): Promise<void> {
    console.log('🚀 Starting complete Firestore setup...');
    
    const existingData = await this.checkExistingData();
    
    if (existingData.hasData) {
      console.log(`ℹ️ Found ${existingData.count} existing apartments in Firestore`);
      const shouldOverwrite = window.confirm(
        `Es existieren bereits ${existingData.count} Wohnungen in Firestore. Sollen diese überschrieben werden?`
      );
      
      if (!shouldOverwrite) {
        console.log('🛑 Migration aborted by user');
        return;
      }
    }

    await this.migrateApartmentsToFirestore();
    await this.initializeSettings();
    
    console.log('🎉 Complete setup finished successfully!');
  }

  /**
   * Reset all data (use with caution!)
   */
  static async resetAllData(): Promise<void> {
    if (!window.confirm('⚠️ WARNUNG: Dies löscht alle Daten in Firestore! Fortfahren?')) {
      return;
    }

    try {
      // This would require admin permissions
      console.log('🗑️ Data reset would require Firebase Admin SDK');
      console.log('Please use Firebase Console to delete collections manually');
      
      // In production, you'd implement this with Firebase Admin SDK
      // or Cloud Functions due to security rules
      
    } catch (error) {
      console.error('❌ Error resetting data:', error);
      throw error;
    }
  }
}

// Export convenience functions
export const migrateToFirestore = () => MigrationService.completeSetup();
export const checkFirestoreData = () => MigrationService.checkExistingData();
export const resetFirestoreData = () => MigrationService.resetAllData();
export const updateConstructionYears = () => MigrationService.updateConstructionYears(); 